import {
  SDKBase,
  createSDK,
  InstanceWithExtensions,
  TronicSDKExtensionsOption,
  TronicSDKAdditionalConfiguration,
} from 'tronic-provider';
import localForage from 'localforage';
import * as memoryDriver from 'localforage-driver-memory';
import { IframeController, IframeControllerConfig } from './iframe-controller';
import { LauncherController } from './launcher/controller';
import { LauncherConfig } from './launcher/types';
import { ViewModule } from './view-module';
import { ReceiverLoader } from './tracking/receiver-loader';

export * from './iframe-controller';
export * from './view-module';
export * from './launcher/simple-widget';
export * from './utils/css-in-js';
export * from 'tronic-provider';
export { ReceiverLoader } from './tracking/receiver-loader';
export * from './tracking/types';

/**
 * Advanced configuration options for the Tronic SDK
 * @internal
 */
interface TronicSDKConfiguration extends Partial<TronicSDKAdditionalConfiguration> {
  /** Configuration for the launcher widget */
  launcher?: LauncherConfig;
  /** Configuration for the iframe controller */
  iframe?: IframeControllerConfig;
  /** Tracking and analytics configuration */
  tracking?: {
    /** Enable or disable tracking (default: true) */
    enabled?: boolean;
    /** Enable debug mode for tracking */
    debug?: boolean;
    /** API host for tracking endpoints */
    apiHost?: string;
    /** Automatically track initial page view (default: true) */
    initialPageview?: boolean;
    /** Auto-track clicks - true for all, or CSS selector for specific elements */
    trackClicks?: boolean | string;
    /** Auto-track form submissions - true for all, or CSS selector for specific forms */
    trackForms?: boolean | string;
    /** Automatically track outbound link clicks */
    trackOutboundLinks?: boolean;
    /** Automatically track widget interactions */
    autoTrackWidget?: boolean;
  };
}

// Simplified initialization parameters
/**
 * Configuration parameters for initializing the Tronic SDK
 * @public
 */
interface TronicInitParams {
  /** API write key for authentication with Tronic services */
  writeKey: string;
  /** URL for the widget iframe channel */
  channelUrl: string;
  /** Optional API URL for backend services (defaults to production if not provided) */
  apiUrl?: string;
  /** Position of the launcher widget on the page */
  position?: 'bottom-left' | 'bottom-right' | 'top-right';
  /** Theme for the widget appearance */
  theme?: 'light' | 'dark';
  /** Enable or disable tracking functionality (default: true) */
  tracking?: boolean;
  /** Enable debug mode for development (default: false) */
  debug?: boolean;
  /** Auto-track clicks - true for all elements, or CSS selector for specific elements */
  trackClicks?: boolean | string;
  /** Auto-track form submissions - true for all forms, or CSS selector for specific forms */
  trackForms?: boolean | string;
  /** Automatically track outbound link clicks */
  trackOutboundLinks?: boolean;
  /** Automatically track widget interactions */
  autoTrackWidget?: boolean;
}

/**
 * Main Tronic SDK class providing widget functionality and analytics tracking
 *
 * @example
 * ```typescript
 * import { TronicSDK } from 'tronic-sdk';
 *
 * const tronic = TronicSDK.init({
 *   writeKey: 'your-api-key',
 *   channelUrl: 'https://your-channel-url.com',
 *   theme: 'dark',
 *   position: 'bottom-right'
 * });
 *
 * // Track events
 * tronic.trackEvent('button_clicked', { buttonId: 'signup' });
 *
 * // Identify users
 * tronic.identifyUser('user123', { email: 'user@example.com' });
 * ```
 *
 * @public
 */
class TronicSDK extends SDKBase {
  /** View module for managing widget display and interactions */
  public readonly view: ViewModule;
  /** Optional launcher controller for widget positioning and appearance */
  public readonly launcher?: LauncherController;
  /** Tracking module for analytics and user behavior monitoring */
  public readonly tracking: typeof ReceiverLoader;

  /**
   * Initialize Tronic SDK with simplified configuration
   *
   * @param params - Configuration parameters for the SDK
   * @returns Initialized TronicSDK instance
   *
   * @example
   * ```typescript
   * const tronic = TronicSDK.init({
   *   writeKey: 'your-api-key',
   *   channelUrl: 'https://your-channel-url.com',
   *   theme: 'dark',
   *   position: 'bottom-right',
   *   debug: false
   * });
   * ```
   *
   * @public
   */
  static init(params: TronicInitParams): TronicSDK;

  /**
   * Initialize Tronic SDK with legacy API (for backward compatibility)
   *
   * @param apiKey - API key for authentication
   * @param endpoint - Endpoint URL for the widget
   * @param options - Additional configuration options
   * @returns Initialized TronicSDK instance
   *
   * @deprecated Use the simplified init(params) method instead
   * @public
   */
  static init(apiKey: string, endpoint: string, options?: Partial<TronicSDKConfiguration>): TronicSDK;
  static init(
    paramsOrApiKey: TronicInitParams | string,
    endpoint?: string,
    options?: Partial<TronicSDKConfiguration>,
  ): TronicSDK {
    let apiKey: string;
    let endpointUrl: string;
    let config: TronicSDKConfiguration;

    // Check if using new simplified API or legacy API
    if (typeof paramsOrApiKey === 'object' && 'writeKey' in paramsOrApiKey) {
      // New simplified API
      const params = paramsOrApiKey;
      apiKey = params.writeKey;
      endpointUrl = params.channelUrl;

      config = {
        endpoint: endpointUrl,
        launcher: {
          position: params.position || 'bottom-right',
          theme: params.theme || 'light', // Include theme support
        },
        tracking: {
          enabled: params.tracking !== false, // Default true unless explicitly false
          debug: params.debug || false,
          apiHost: params.apiUrl, // Pass apiUrl to tracking config for constructor access
          trackClicks: params.trackClicks,
          trackForms: params.trackForms,
          trackOutboundLinks: params.trackOutboundLinks,
          autoTrackWidget: params.autoTrackWidget,
        },
      };
    } else {
      // Legacy API for backward compatibility
      apiKey = paramsOrApiKey;
      endpointUrl = endpoint!;
      config = {
        endpoint: endpointUrl,
        launcher: {
          position: options?.launcher?.position || 'bottom-right',
          theme: options?.launcher?.theme || 'light', // Include theme support in legacy API too
        },
        tracking: {
          enabled: options?.tracking?.enabled !== false,
          debug: options?.tracking?.debug || false,
        },
        ...options,
      };
    }

    const instance = new TronicSDK(apiKey, config);

    // Initialize tracking if enabled
    if (config.tracking?.enabled !== false) {
      // Use apiUrl if provided (new API), otherwise extract from endpoint/channelUrl (legacy)
      let apiHost: string;

      if (typeof paramsOrApiKey === 'object' && 'writeKey' in paramsOrApiKey && paramsOrApiKey.apiUrl) {
        // New API with explicit apiUrl
        apiHost = paramsOrApiKey.apiUrl;
      } else {
        // Legacy: Extract base URL from endpoint, but for staging environments use proper API host
        if (endpointUrl.includes('staging-wallet.tronic.app')) {
          // For staging channelUrl, use staging API host
          apiHost = 'https://stage-api.tronic.app';
        } else {
          try {
            const url = new URL(endpointUrl);
            apiHost = `${url.protocol}//${url.host}`;
          } catch (e) {
            // If endpoint is not a valid URL, use as-is
            apiHost = endpointUrl.replace(/\/[^/]*$/, ''); // Remove last path segment if any
          }
        }
      }

      ReceiverLoader.initialize({
        apiKey,
        apiHost,
        debug: config.tracking?.debug || false,
        initialPageview: config.tracking?.initialPageview,
        trackClicks: config.tracking?.trackClicks,
        trackForms: config.tracking?.trackForms,
        trackOutboundLinks: config.tracking?.trackOutboundLinks,
        autoTrackWidget: config.tracking?.autoTrackWidget,
      });

      // Set up automatic user identification when auth state changes
      instance.setupUserTracking();
    }

    if (instance.launcher) {
      instance.launcher.mount();
    }

    return instance;
  }

  constructor(apiKey: string, options: TronicSDKConfiguration = {}) {
    const configWithDefaults: TronicSDKAdditionalConfiguration = {
      endpoint: options.endpoint || 'https://api.tronic.app',
      ...options,
    };
    super(apiKey, configWithDefaults);
    this.view = new ViewModule(this);
    this.tracking = ReceiverLoader;

    // Initialize tracking if enabled and not already initialized
    if (options.tracking?.enabled !== false && !ReceiverLoader.isInitialized()) {
      // Use apiHost from tracking config if provided, otherwise extract from endpoint
      let apiHost = options.tracking?.apiHost || 'https://api.tronic.app';

      if (!options.tracking?.apiHost) {
        // Fallback: Try to extract from endpoint
        const endpoint = options.endpoint || 'https://api.tronic.app';
        try {
          const url = new URL(endpoint);
          apiHost = `${url.protocol}//${url.host}`;
        } catch (e) {
          apiHost = endpoint.replace(/\/[^/]*$/, '');
        }
      }

      ReceiverLoader.initialize({
        apiKey,
        apiHost,
        debug: options.tracking?.debug || false,
        initialPageview: options.tracking?.initialPageview,
        trackClicks: options.tracking?.trackClicks,
        trackForms: options.tracking?.trackForms,
        trackOutboundLinks: options.tracking?.trackOutboundLinks,
        autoTrackWidget: options.tracking?.autoTrackWidget,
      });

      // Set up automatic user identification
      this.setupUserTracking();
    }

    if (options?.iframe) {
      const overlay = this.overlay as IframeController;
      if (overlay && typeof overlay.configure === 'function') {
        overlay.configure(options.iframe);
      }
    }

    if (options?.launcher) {
      this.launcher = new LauncherController(this as any, options.launcher);
    }
  }

  /**
   * Track a custom event with optional properties
   *
   * @param eventName - Name of the event to track
   * @param properties - Optional properties to associate with the event
   *
   * @example
   * ```typescript
   * tronic.trackEvent('button_clicked', {
   *   buttonId: 'signup',
   *   page: 'homepage',
   *   variant: 'primary'
   * });
   * ```
   *
   * @public
   */
  public trackEvent(eventName: string, properties?: Record<string, any>): void {
    ReceiverLoader.trackEvent(eventName, properties);
  }

  /**
   * Track a page view with optional properties
   *
   * @param pageName - Optional name of the page (defaults to current URL)
   * @param properties - Optional properties to associate with the page view
   *
   * @example
   * ```typescript
   * tronic.trackPage('Product Page', {
   *   category: 'electronics',
   *   productId: '12345'
   * });
   * ```
   *
   * @public
   */
  public trackPage(pageName?: string, properties?: Record<string, any>): void {
    ReceiverLoader.trackPage(pageName, properties);
  }

  /**
   * Identify a user with traits for personalized tracking
   *
   * @param userId - Unique identifier for the user
   * @param traits - Optional user traits and properties
   *
   * @example
   * ```typescript
   * tronic.identifyUser('user123', {
   *   email: 'user@example.com',
   *   firstName: 'John',
   *   plan: 'premium'
   * });
   * ```
   *
   * @public
   */
  public identifyUser(userId: string, traits?: Record<string, any>): void {
    ReceiverLoader.identifyUser(userId, traits);
  }

  /**
   * Track clicks on specific elements
   *
   * @param selector - CSS selector, element, or array of elements to track
   * @param eventName - Name of the event to track when clicked
   * @param properties - Optional properties to associate with the click event
   *
   * @example
   * ```typescript
   * tronic.trackClick('.cta-button', 'CTA Clicked', {
   *   section: 'hero',
   *   variant: 'primary'
   * });
   * ```
   *
   * @public
   */
  public trackClick(selector: string | Element | Element[], eventName: string, properties?: Record<string, any>): void {
    ReceiverLoader.trackClick(selector, eventName, properties);
  }

  /**
   * Track form submissions on specific forms
   *
   * @param selector - CSS selector, form element, or array of form elements to track
   * @param eventName - Name of the event to track when form is submitted
   * @param properties - Optional properties to associate with the form submission
   *
   * @example
   * ```typescript
   * tronic.trackForm('#newsletter-form', 'Newsletter Signup', {
   *   source: 'footer',
   *   variant: 'compact'
   * });
   * ```
   *
   * @public
   */
  public trackForm(
    selector: string | HTMLFormElement | HTMLFormElement[],
    eventName: string,
    properties?: Record<string, any>,
  ): void {
    ReceiverLoader.trackForm(selector, eventName, properties);
  }

  /**
   * Track clicks on link elements
   *
   * @param selector - CSS selector, element, or array of elements to track
   * @param eventName - Name of the event to track when link is clicked
   * @param properties - Optional properties to associate with the link click
   *
   * @example
   * ```typescript
   * tronic.trackLink('.external-link', 'External Link Clicked', {
   *   destination: 'partner-site',
   *   section: 'resources'
   * });
   * ```
   *
   * @public
   */
  public trackLink(selector: string | Element | Element[], eventName: string, properties?: Record<string, any>): void {
    ReceiverLoader.trackLink(selector, eventName, properties);
  }

  /**
   * Track screen or page transitions in single-page applications
   *
   * @param name - Name of the screen or view
   * @param properties - Optional properties to associate with the screen view
   *
   * @example
   * ```typescript
   * tronic.screen('Dashboard', {
   *   userId: 'user123',
   *   section: 'analytics'
   * });
   * ```
   *
   * @public
   */
  public screen(name: string, properties?: Record<string, any>): void {
    ReceiverLoader.screen(name, properties);
  }

  /**
   * Associate a user with a group or organization
   *
   * @param groupId - Unique identifier for the group
   * @param traits - Optional traits and properties of the group
   *
   * @example
   * ```typescript
   * tronic.group('company123', {
   *   name: 'Acme Corp',
   *   plan: 'enterprise',
   *   employees: 500
   * });
   * ```
   *
   * @public
   */
  public group(groupId: string, traits?: Record<string, any>): void {
    ReceiverLoader.group(groupId, traits);
  }

  /**
   * Create an alias to link a new user ID with a previous ID
   *
   * @param userId - New user identifier
   * @param previousId - Previous user identifier to link (optional)
   *
   * @example
   * ```typescript
   * tronic.alias('user123', 'anonymous-uuid-456');
   * ```
   *
   * @public
   */
  public alias(userId: string, previousId?: string): void {
    ReceiverLoader.alias(userId, previousId);
  }

  /**
   * Reset the current user session and clear stored user data
   *
   * @example
   * ```typescript
   * tronic.resetUser(); // Clear user session on logout
   * ```
   *
   * @public
   */
  public resetUser(): void {
    ReceiverLoader.resetUser();
  }

  /**
   * Set up automatic user tracking when authentication state changes
   */
  private setupUserTracking(): void {
    if (!ReceiverLoader.isInitialized()) {
      return;
    }

    // Listen for authentication events from localStorage
    const checkAuthState = () => {
      try {
        // Check common auth storage keys
        const authKeys = [
          'tronic_cached_profile_data',
          'tronic_cached_auth_state',
          'tronic_user_session',
          'tronic_auth_token',
        ];

        for (const key of authKeys) {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const data = JSON.parse(stored);
              if (data && (data.profile || data.user || data.id || data.email)) {
                // Extract user information
                const profile = data.profile || data.user || data;
                const userId = profile.id || profile.user_id || profile.email || 'anonymous';

                ReceiverLoader.identifyUser(userId, {
                  email: profile.email,
                  firstName: profile.firstName || profile.first_name,
                  lastName: profile.lastName || profile.last_name,
                  name: profile.name || `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
                  walletAddress: profile.walletAddress || profile.wallet_address,
                  emailVerified: profile.emailVerified || profile.email_verified,
                  source: 'tronic_sdk_auto',
                });

                return; // Only identify once
              }
            } catch (e) {
              // Invalid JSON, continue to next key
            }
          }
        }
      } catch (error) {
        console.debug('[TronicSDK] Error checking auth state for tracking:', error);
      }
    };

    // Initial check
    setTimeout(checkAuthState, 500);

    // Listen for storage changes (from other tabs/windows)
    window.addEventListener('storage', (e) => {
      if (e.key && e.key.startsWith('tronic_')) {
        setTimeout(checkAuthState, 100);
      }
    });

    // Listen for focus events (user returning to tab)
    window.addEventListener('focus', () => {
      setTimeout(checkAuthState, 100);
    });
  }
}

export { TronicSDK };

export const Tronic = createSDK(TronicSDK, {
  platform: 'web',
  sdkName: 'tronic-sdk',
  version: process.env.WEB_VERSION!,
  ViewController: IframeController,
  configureStorage: async () => {
    const lf = localForage.createInstance({
      name: 'TronicAuthLocalStorageDB',
      storeName: 'TronicAuthLocalStorage',
    });

    await lf.defineDriver(memoryDriver);
    await lf.setDriver([localForage.INDEXEDDB, localForage.LOCALSTORAGE, memoryDriver._driver]);

    return lf;
  },
});

export type Tronic<T extends TronicSDKExtensionsOption<any> = TronicSDKExtensionsOption> = InstanceWithExtensions<
  TronicSDK,
  T
>;
